import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useLang } from "../i18n";
import { toast } from "sonner";
import { Download, Cpu, GitCompareArrows, FolderOpen, Hammer, Play, ShieldAlert, Settings2, KeyRound, Lock, Globe } from "lucide-react";

const T = {
  it: {
    kicker: "Area riservata",
    title: "I tuoi Expert Advisor (MT4)",
    intro: "Qui trovi i file compilati .ex4 dei sistemi che hai acquistato, la registrazione della licenza sul tuo conto MT4 e la guida di installazione completa.",
    not_owned_title: "Non hai ancora acquistato questo EA",
    not_owned_cta: "Scopri e acquista →",
    download: "Scarica",
    file_pending: "File compilato in preparazione: sarà disponibile qui a breve. Riceverai comunque l'accesso automatico.",
    lic_title: "Licenza conto MT4",
    lic_desc: "L'EA opera SOLO sul conto MT4 registrato qui. Inserisci il numero del conto (lo vedi in MT4 in alto o nel Navigator).",
    lic_placeholder: "Numero conto MT4 (es. 1234567)",
    lic_btn: "Registra conto",
    lic_active: "Licenza attiva sul conto",
    lic_help: "Per spostare la licenza su un altro conto contatta l'assistenza.",
    hydra_tag: "Arbitraggio statistico a 5 spread — si applica a UN grafico qualsiasi",
    atlas_tag: "Trend-following — si applica ai grafici D1 di Oro, Argento, US500, US30",
    guide_title: "Guida all'installazione passo-passo",
    steps: [
      ["1. Scarica il file .ex4", "Clicca \u201cScarica\u201d qui sopra: il file compilato viene salvato nella cartella Download."],
      ["2. Apri la cartella dati di MT4", "In MetaTrader 4: menu File → \u201cApri scheda dati\u201d (Open Data Folder)."],
      ["3. Copia il file in MQL4/Experts", "Entra in MQL4 → Experts e incolla il file .ex4. Nessuna compilazione necessaria: è già pronto."],
      ["4. Autorizza il server licenze", "In MT4: Strumenti → Opzioni → Consulenti Esperti → spunta \u201cConsenti WebRequest per i seguenti URL\u201d e aggiungi l'indirizzo del sito BundStrategy (riportato negli input dell'EA come LicenseServer). PASSAGGIO OBBLIGATORIO: senza, l'EA resta bloccato e non apre operazioni."],
      ["5. Riavvia MT4 e trascina l'EA sul grafico", "L'EA appare nel Navigator sotto \u201cConsulenti Esperti\u201d. HYDRA: su UN grafico D1 qualsiasi. ATLAS: sul grafico D1 di ciascun mercato."],
      ["6. Abilita il trading automatico", "Spunta \u201cConsenti trading live\u201d nella finestra di dialogo e verifica che il pulsante \u201cTrading Automatico\u201d nella barra sia VERDE (faccina sorridente sul grafico)."],
      ["7. Valida in DEMO 1-3 mesi", "Registra qui anche il conto demo? No: la licenza vale per UN conto. Consiglio: registra prima il conto DEMO, valida 1-3 mesi, poi chiedi all'assistenza lo spostamento sul reale (gratuito). Il tester MT4 non supporta EA multi-valuta: la demo è l'unico test valido per HYDRA."],
    ],
    broker_title: "Compatibilità con altri broker (input simboli di HYDRA)",
    broker_text: "Ogni broker chiama i simboli in modo diverso. HYDRA ha tutti i nomi configurabili negli input, senza toccare il codice:",
    broker_rows: [
      ["Suffix", "Suffisso delle coppie FX del tuo conto (Axi Pro: \u201c.pro\u201d). Vuoto se il broker non usa suffissi."],
      ["Pair1_A / Pair1_B", "Nomi di WTI e Brent (default Axi: USOIL / UKOIL). Verifica nel Market Watch (Ctrl+M → \u201cMostra tutti\u201d)."],
      ["Pair2..5_A / _B", "Nomi delle coppie FX. Cambiali solo se il tuo broker usa nomi diversi."],
      ["Pair1..5_Enabled", "Disattiva una coppia non offerta dal broker: le altre continuano a operare."],
      ["RiskPercent", "Rischio per trade (default 2%). I lotti si calcolano da soli in base al saldo."],
      ["LicenseServer", "Indirizzo del server licenze: non modificarlo, e aggiungilo alla whitelist WebRequest (passo 4)."],
    ],
    risk_title: "Avvertenze",
    risk_items: [
      "Testare sempre in conto demo prima del reale (1-3 mesi consigliati).",
      "I risultati dei backtest non garantiscono risultati futuri.",
      "L'EA opera in autonomia ma va supervisionato: controlla il tab \u201cEsperti\u201d di MT4 per i log.",
      "Il terminale deve restare acceso (o usa un VPS): HYDRA valuta gli spread ogni giorno dopo la mezzanotte del server.",
    ],
  },
  en: {
    kicker: "Members area",
    title: "Your Expert Advisors (MT4)",
    intro: "Here you find the compiled .ex4 files of the systems you purchased, the license registration for your MT4 account and the full installation guide.",
    not_owned_title: "You haven't purchased this EA yet",
    not_owned_cta: "Discover & buy →",
    download: "Download",
    file_pending: "Compiled file being prepared: it will be available here shortly. Your access is already granted.",
    lic_title: "MT4 account license",
    lic_desc: "The EA trades ONLY on the MT4 account registered here. Enter your account number (shown at the top of MT4 or in the Navigator).",
    lic_placeholder: "MT4 account number (e.g. 1234567)",
    lic_btn: "Register account",
    lic_active: "License active on account",
    lic_help: "To move the license to another account contact support.",
    hydra_tag: "5-spread statistical arbitrage — attach to ONE chart of any symbol",
    atlas_tag: "Trend following — attach to D1 charts of Gold, Silver, US500, US30",
    guide_title: "Step-by-step installation guide",
    steps: [
      ["1. Download the .ex4 file", "Click \u201cDownload\u201d above: the compiled file is saved to your Downloads folder."],
      ["2. Open the MT4 data folder", "In MetaTrader 4: File menu → \u201cOpen Data Folder\u201d."],
      ["3. Copy the file into MQL4/Experts", "Go into MQL4 → Experts and paste the .ex4 file. No compilation needed: it's ready."],
      ["4. Whitelist the license server", "In MT4: Tools → Options → Expert Advisors → tick \u201cAllow WebRequest for listed URL\u201d and add the BundStrategy site address (shown in the EA inputs as LicenseServer). MANDATORY STEP: without it the EA stays locked and opens no trades."],
      ["5. Restart MT4 and drag the EA onto the chart", "The EA appears in the Navigator under \u201cExpert Advisors\u201d. HYDRA: onto ONE D1 chart of any symbol. ATLAS: onto each market's D1 chart."],
      ["6. Enable auto trading", "Tick \u201cAllow live trading\u201d in the dialog and make sure the \u201cAutoTrading\u201d toolbar button is GREEN (smiley face on the chart)."],
      ["7. Validate on DEMO for 1-3 months", "Should you register the demo account here? The license covers ONE account. Tip: register the DEMO account first, validate for 1-3 months, then ask support to move it to your live account (free). The MT4 tester doesn't support multi-currency EAs: demo forward-testing is the only valid test for HYDRA."],
    ],
    broker_title: "Other-broker compatibility (HYDRA symbol inputs)",
    broker_text: "Every broker names symbols differently. HYDRA exposes every name as an input, no code changes:",
    broker_rows: [
      ["Suffix", "FX pair suffix of your account (Axi Pro: \u201c.pro\u201d). Empty if your broker uses none."],
      ["Pair1_A / Pair1_B", "WTI and Brent names (Axi defaults: USOIL / UKOIL). Check Market Watch (Ctrl+M → \u201cShow All\u201d)."],
      ["Pair2..5_A / _B", "FX pair names. Change only if your broker uses different ones."],
      ["Pair1..5_Enabled", "Disable a pair your broker doesn't offer: the others keep trading."],
      ["RiskPercent", "Per-trade risk (default 2%). Lots are auto-sized from the balance."],
      ["LicenseServer", "License server address: do not change it, and add it to the WebRequest whitelist (step 4)."],
    ],
    risk_title: "Warnings",
    risk_items: [
      "Always test on demo before going live (1-3 months recommended).",
      "Backtest results do not guarantee future results.",
      "The EA runs autonomously but should be supervised: check the MT4 \u201cExperts\u201d tab for logs.",
      "The terminal must stay on (or use a VPS): HYDRA evaluates spreads daily after server midnight.",
    ],
  },
};

const PRODUCTS = [
  { key: "hydra", name: "HYDRA StatArb", icon: GitCompareArrows, accent: "cyan", file: "HYDRA_StatArb.ex4" },
  { key: "atlas", name: "ATLAS Breakout", icon: Cpu, accent: "amber", file: "ATLAS_Breakout.ex4" },
];

function LicenseForm({ product, license, onDone, t }) {
  const [acc, setAcc] = useState("");
  const [busy, setBusy] = useState(false);
  if (license) {
    return (
      <p data-testid={`ea-license-active-${product}`} className="text-xs text-emerald-400 mt-3 leading-relaxed">
        <KeyRound className="w-3.5 h-3.5 inline mr-1.5 align-[-2px]" />
        {t.lic_active} <span className="font-mono font-bold">{license.mt4_account}</span>
        <span className="text-slate-500"> — {t.lic_help}</span>
      </p>
    );
  }
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/ea/license", { product, mt4_account: parseInt(acc, 10) });
      toast.success("Licenza registrata");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 border border-white/10 bg-black/30 p-4">
      <p className="text-xs font-semibold text-white flex items-center gap-2"><KeyRound className="w-3.5 h-3.5 text-emerald-400" /> {t.lic_title}</p>
      <p className="text-xs text-slate-500 mt-1 leading-relaxed">{t.lic_desc}</p>
      <div className="flex gap-2 mt-3">
        <input data-testid={`ea-license-input-${product}`} type="number" placeholder={t.lic_placeholder} value={acc}
          onChange={(e) => setAcc(e.target.value)}
          className="flex-1 bg-[#111111] border border-white/15 px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" />
        <button data-testid={`ea-license-submit-${product}`} onClick={submit} disabled={!acc || busy}
          className="border border-emerald-500/40 text-emerald-400 px-4 py-2 text-xs uppercase tracking-widest hover:bg-emerald-500/10 disabled:opacity-40">
          {t.lic_btn}
        </button>
      </div>
    </div>
  );
}

export default function Downloads() {
  const { lang } = useLang();
  const t = T[lang] || T.it;
  const [my, setMy] = useState(null);

  const refresh = () => api.get("/ea/my").then((r) => setMy(r.data)).catch(() => setMy({ owned: [], licenses: [], files_available: {} }));
  useEffect(() => { refresh(); }, []);

  const download = async (p, filename) => {
    try {
      const res = await api.get(`/ea/download/${p}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      const detail = e.response?.data instanceof Blob ? await e.response.data.text().then((s) => { try { return JSON.parse(s).detail; } catch { return s; } }) : e.response?.data?.detail;
      toast.error(formatApiErrorDetail(detail));
    }
  };

  const stepIcons = [Download, FolderOpen, FolderOpen, Globe, Play, Play, ShieldAlert];

  return (
    <div data-testid="downloads-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <p className="text-xs font-mono uppercase tracking-[0.25em] text-emerald-400">{t.kicker}</p>
      <h1 className="font-heading font-bold text-3xl sm:text-4xl text-white mt-2">{t.title}</h1>
      <p className="text-slate-400 leading-relaxed mt-4 max-w-3xl">{t.intro}</p>

      <div className="grid md:grid-cols-2 gap-6 mt-10">
        {PRODUCTS.map(({ key, name, icon: Icon, accent, file }) => {
          const owned = my?.owned?.includes(key);
          const lic = my?.licenses?.find((l) => l.product === key);
          const fileReady = my?.files_available?.[key];
          const col = accent === "cyan" ? "text-cyan-400" : "text-amber-400";
          const border = accent === "cyan" ? "border-cyan-500/25 bg-cyan-500/[0.04]" : "border-amber-500/25 bg-amber-500/[0.04]";
          return (
            <div key={key} data-testid={`ea-dl-card-${key}`} className={`rounded-xl border ${border} p-6`}>
              <div className="flex items-center gap-3">
                <Icon className={`w-7 h-7 ${col}`} />
                <div>
                  <h3 className="font-heading font-bold text-xl text-white">{name}</h3>
                  <p className={`text-[11px] font-mono uppercase tracking-widest ${col} opacity-80`}>{key === "hydra" ? t.hydra_tag : t.atlas_tag}</p>
                </div>
              </div>
              {my === null ? null : owned ? (
                <>
                  {fileReady ? (
                    <button data-testid={`ea-download-btn-${key}`} onClick={() => download(key, file)}
                      className={`mt-5 w-full inline-flex items-center justify-center gap-2 rounded px-4 py-2.5 font-medium text-sm text-black transition-colors ${accent === "cyan" ? "bg-cyan-400 hover:bg-cyan-300" : "bg-amber-400 hover:bg-amber-300"}`}>
                      <Download className="w-4 h-4" /> {t.download} {file}
                    </button>
                  ) : (
                    <p data-testid={`ea-file-pending-${key}`} className="mt-5 text-xs text-amber-400/90 border border-amber-500/20 bg-amber-500/5 p-3 leading-relaxed">{t.file_pending}</p>
                  )}
                  <LicenseForm product={key} license={lic} onDone={refresh} t={t} />
                </>
              ) : (
                <div data-testid={`ea-not-owned-${key}`} className="mt-5 border border-white/10 bg-black/30 p-4">
                  <p className="text-sm text-slate-400 flex items-center gap-2"><Lock className="w-4 h-4 text-slate-500" /> {t.not_owned_title}</p>
                  <Link to="/expert-advisors" data-testid={`ea-store-link-${key}`} className={`inline-block mt-2 text-sm font-semibold ${col} hover:underline`}>{t.not_owned_cta}</Link>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <section className="mt-14">
        <h2 className="font-heading font-bold text-lg md:text-lg text-white">{t.guide_title}</h2>
        <ol className="mt-6 space-y-4">
          {t.steps.map(([title, text], i) => {
            const Icon = stepIcons[i] || Play;
            return (
              <li key={i} data-testid={`install-step-${i + 1}`} className="flex gap-4 rounded-lg border border-white/10 bg-white/[0.02] p-4">
                <Icon className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-white">{title}</p>
                  <p className="text-sm text-slate-400 leading-relaxed mt-1">{text}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      <section className="mt-14">
        <div className="flex items-center gap-2">
          <Settings2 className="w-5 h-5 text-cyan-400" />
          <h2 className="font-heading font-bold text-lg md:text-lg text-white">{t.broker_title}</h2>
        </div>
        <p className="text-sm text-slate-400 leading-relaxed mt-3 max-w-3xl">{t.broker_text}</p>
        <div data-testid="broker-symbols-table" className="mt-5 rounded-lg border border-white/10 overflow-hidden">
          {t.broker_rows.map(([k, v], i) => (
            <div key={i} className={`flex flex-col sm:flex-row gap-1 sm:gap-6 p-4 ${i % 2 ? "bg-white/[0.02]" : ""} ${i ? "border-t border-white/5" : ""}`}>
              <code className="text-cyan-300 text-sm font-mono shrink-0 sm:w-44">{k}</code>
              <p className="text-sm text-slate-400 leading-relaxed">{v}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14 rounded-xl border border-red-500/20 bg-red-500/[0.04] p-6">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-red-400" />
          <h2 className="font-heading font-bold text-lg md:text-lg text-white">{t.risk_title}</h2>
        </div>
        <ul className="mt-4 space-y-2">
          {t.risk_items.map((r, i) => (
            <li key={i} className="text-sm text-slate-400 leading-relaxed flex gap-2">
              <span className="text-red-400 shrink-0">•</span>{r}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
